import { useEffect, useState } from "react";
import { Download, Mail, Leaf, Circle, MinusCircle, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ItemRow, HostingPicker, BaaSection } from "./ReadinessParts";

const when = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "");

export const STATE_ICON = {
  ready: <Leaf className="w-4 h-4 text-emerald-700 fill-emerald-200" />,
  on_track: <Circle className="w-4 h-4 text-amber-600" />,
  not_yet: <Circle className="w-4 h-4 text-stone-300" />,
  not_needed: <MinusCircle className="w-4 h-4 text-stone-300" />,
};

export function ReadinessCard() {
  const [b, setB] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api.get("/admin/readiness").then((r) => setB(r.data)).catch((e) => setErr(e?.response?.status || "err")); }, []);
  if (err) return null;
  if (!b) return <section className="warm-card p-6 text-sm text-stone-500" data-testid="readiness-card">Loading readiness…</section>;
  const call = (fn) => fn().then((r) => setB(r.data)).catch((e) => toast.error(e?.response?.data?.detail || "Couldn't save"));
  const p = b.progress;
  return (
    <section className="warm-card p-6 space-y-5" data-testid="readiness-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-rose-800">Privacy posture</div>
          <div className="font-display text-2xl font-bold text-stone-900 mt-1" data-testid="readiness-progress">{p.ready} of {p.total} ready <span className="text-base font-normal text-stone-500">· {p.auto_ready} auto · {p.manual_ready} manual</span></div>
          <p className="text-sm text-stone-600 mt-1">"Consumer health data (journals, wellbeing) is handled as sensitive: FTC Act, FTC Health Breach Notification Rule and Oregon's consumer privacy rules are assumed to apply. A working aid for counsel."</p>
        </div>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/readiness/pdf`} target="_blank" rel="noreferrer" data-testid="readiness-pdf"
          className="inline-flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-full bg-stone-900 text-white hover:bg-rose-800"><Download className="w-3.5 h-3.5" /> Export PDF</a>
      </div>
      <HostingPicker hosting={b.hosting} options={b.hosting_options} baaReply={b.baa?.reply} onPick={(choice) => call(() => api.patch("/admin/readiness/hosting", { choice }))} />
      <BaaSection baa={b.baa} outcomes={b.baa_outcomes} mailto={b.mailto} when={when}
        onSent={() => call(() => api.post("/admin/readiness/baa/sent"))} onClear={() => call(() => api.delete("/admin/readiness/baa/sent"))}
        onReply={(body) => call(() => api.patch("/admin/readiness/baa/reply", body))} />
      {b.blocking.length > 0 && <div className="text-xs text-stone-500" data-testid="readiness-blocking"><span className="font-semibold text-stone-700">Not yet:</span> {b.blocking.join(" · ")}</div>}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1">Checked by the app</div>
        <ul className="divide-y divide-stone-100">{b.auto.map((i) => <ItemRow key={i.key} item={i} />)}</ul>
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1 flex items-center gap-1"><LinkIcon className="w-3 h-3" /> Ticked by you</div>
        <ul className="divide-y divide-stone-100">{b.manual.map((i) => <ItemRow key={i.key} item={i} when={when} onSave={(body) => call(() => api.patch(`/admin/readiness/items/${i.key}`, body))} />)}</ul>
      </div>
      <p className="text-[11px] text-stone-400 flex items-center gap-1"><Mail className="w-3 h-3" /> Ticks, hosting changes, BAA records and exports are audit-logged. A working aid for counsel, not legal advice.</p>
    </section>
  );
}
